import { apiFetch } from "@/lib/api";
import type { NotificationFeedDto } from "@/lib/types";
import { useSession } from "@/components/providers/session-provider";
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

interface NotificationsContextValue {
  unreadCount: number;
  refreshUnread: () => Promise<void>;
  setUnreadCount: (n: number) => void;
}

const NotificationsContext = createContext<NotificationsContextValue | null>(null);

export function useNotifications(): NotificationsContextValue {
  const ctx = useContext(NotificationsContext);
  if (!ctx)
    throw new Error("useNotifications must be used within NotificationsProvider");
  return ctx;
}

export function NotificationsProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const [unreadCount, setUnreadCount] = useState(0);

  const refreshUnread = useCallback(async () => {
    if (me?.authenticated !== true) {
      setUnreadCount(0);
      return;
    }
    try {
      const feed = await apiFetch<NotificationFeedDto>(
        "/api/notifications?unreadOnly=true&take=1",
        { suppressAuthRedirect: true },
      );
      setUnreadCount(feed.unreadCount);
    } catch {
      // Non-fatal.
    }
  }, [me?.authenticated]);

  useEffect(() => {
    void refreshUnread();
    if (me?.authenticated !== true) return;
    const interval = setInterval(() => void refreshUnread(), 60_000);
    return () => clearInterval(interval);
  }, [me?.authenticated, refreshUnread]);

  // This provider wraps the whole app, so a fresh object here would re-render every
  // consumer on each render of any ancestor.
  const value = useMemo(
    () => ({ unreadCount, refreshUnread, setUnreadCount }),
    [unreadCount, refreshUnread],
  );

  return (
    <NotificationsContext.Provider value={value}>
      {children}
    </NotificationsContext.Provider>
  );
}
